import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Clients from '../components/Clients';
import Cta from '../components/Cta';
import Footer from '../components/Footer';
import Hero from '../components/Hero';
import Intro from '../components/Intro';
import Portfolio from '../components/Portfolio';
import Services from '../components/Services';
import SEO from '../components/SEO';
import AnimatedBackground from '../components/AnimatedBackground';
import { usePerformance } from '../hooks/usePerformance';

const Home = () => {
    const containerRef = useRef(null);
    const sectionRefs = useRef([]);
    const { prefersReducedMotion } = usePerformance();
    
    // Global scroll progress for overall page effects
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    // Simplified scroll-based effects - less aggressive for better performance
    const backgroundIntensity = useTransform(scrollYProgress, [0, 0.5, 1], [0.6, 0.8, 0.6]);
    
    // Removed heavy transforms: hue rotation, blur, scale, rotate for better performance

    // Simple section wrapper - no scroll-based transitions
    const SectionWithScroll = ({ index, children, className = "" }) => {
        const sectionRef = useRef(null);
        
        React.useEffect(() => {
            if (sectionRef.current) {
                sectionRefs.current[index] = sectionRef.current;
            }
        }, [index]);

        return (
            <section
                ref={sectionRef}
                className={className}
                style={{
                    position: 'relative',
                    isolation: 'isolate',
                }}
            >
                {children}
            </section>
        );
    };

    return (
        <main 
            className="relative scroll-smooth" 
            ref={containerRef}
            style={{
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain'
            }}
        >
            {/* Simplified dynamic background - less aggressive for better performance */}
            <motion.div
                style={{
                    opacity: prefersReducedMotion ? 0.5 : backgroundIntensity,
                    willChange: 'opacity',
                }}
            >
                <AnimatedBackground scrollYProgress={scrollYProgress} />
            </motion.div>
            
            {/* Removed heavy scroll effects: gradient overlay, particles, wave distortion, velocity blur for better performance */}
            
            {/* Scroll Progress Indicator */}
            <motion.div
                className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 dark:from-white dark:via-gray-300 dark:to-white origin-left z-50"
                style={{ scaleX: scrollYProgress }}
            />

            <SEO 
                title="Tomorrow Global - Agence Média 360 | Communication & Publicité"
                description="Tomorrow Global, agence média 360 dédiée à l'excellence et à l'innovation. Nous créons des stratégies novatrices et intégrons les dernières avancées technologiques pour garantir le succès de nos clients. Services: achat média, production audiovisuelle, marketing digital, influence marketing."
                keywords="agence média, publicité, achat média, production audiovisuelle, marketing digital, influence marketing, communication, stratégie marketing, campagnes publicitaires, ROI, Impact 2.0, Tomorrow Global, Tunisie, Canada, agence communication Tunisie, agence publicité Canada"
            />
            
            <div className="relative z-10">
                <SectionWithScroll index={0} className="min-h-screen">
                    <Hero />
                </SectionWithScroll>

                <SectionWithScroll index={1} className="min-h-screen py-20">
                    <Intro />
                </SectionWithScroll>

                <SectionWithScroll index={2} className="min-h-screen py-20">
                    <Services />
                </SectionWithScroll>

                <SectionWithScroll index={3} className="min-h-screen py-20">
                    <Portfolio />
                </SectionWithScroll>

                <SectionWithScroll index={4} className="min-h-screen py-20">
                    <Clients />
                </SectionWithScroll>

                <SectionWithScroll index={5} className="min-h-screen py-20">
                    <Cta/>
                </SectionWithScroll>

                <motion.section
                    ref={(el) => {
                        if (el) sectionRefs.current[6] = el;
                    }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6 }}
                >
                    <Footer />
                </motion.section>
            </div>
        </main>
    )
}

export default Home;

